import React from 'react';

import { Steps, theme } from 'antd';
import { TFuncKey, useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import {
  EpdDocumentKind,
  EpdDocumentSchema,
  EpdSigningRequestStatus,
  EpdState,
  EpdTitleCode,
  EpdTitleSchema,
  EpdTitleStatus,
  EpdWorkflowSchema,
} from '@app/models/orders';
import { formatDateAndTime, fromUtc } from '@app/services/date';

interface IProps {
  workflow: EpdWorkflowSchema;
  documents: EpdDocumentSchema[];
}

interface IStage {
  key: TFuncKey<'epd', 'stages'>;
  passed: boolean;
  date?: string | null;
}

const FAILED_STATES = [EpdState.Error, EpdState.NeedsData];
const DRAFT_READY = [EpdTitleStatus.AwaitingSignature, EpdTitleStatus.Sending, EpdTitleStatus.Sent];

const findTitle = (document: EpdDocumentSchema | undefined, code: EpdTitleCode) =>
  document?.titles.find((title) => title.title === code);

const isSent = (title?: EpdTitleSchema) => title?.status === EpdTitleStatus.Sent;

const sentDate = (title?: EpdTitleSchema) => (isSent(title) ? title?.sent_at || title?.status_changed_at : undefined);

const finalStage = (workflow: EpdWorkflowSchema, document: EpdDocumentSchema): IStage => {
  const t3 = findTitle(document, EpdTitleCode.T3);
  if (document.state === EpdState.Revoked) return { key: 'revoked', passed: true, date: sentDate(t3) };
  if (document.state === EpdState.Cancelled) return { key: 'cancelled', passed: true, date: workflow.closed_at };
  const revoking =
    !!t3 ||
    document.requests.some(
      (request) => request.title === EpdTitleCode.T3 && request.status !== EpdSigningRequestStatus.Cancelled
    );
  if (revoking) return { key: 'revoked', passed: false };
  return { key: 'completed', passed: workflow.status === 'completed', date: workflow.closed_at };
};

export const OrderStages: React.FC<IProps> = ({ workflow, documents }) => {
  const epdT = useTranslation(NSKeys.epd);
  const { token } = theme.useToken();
  const document = documents.find((item) => item.kind === EpdDocumentKind.ForwardingOrder);
  if (!document) return null;
  const receipt = documents.find(
    (item) => item.kind === EpdDocumentKind.ForwardingReceipt && item.parent_document_uid === document.uid
  );
  const t1 = findTitle(document, EpdTitleCode.T1);
  const t2 = findTitle(document, EpdTitleCode.T2);
  const receiptTitle = findTitle(receipt, EpdTitleCode.Receipt);
  const final = finalStage(workflow, document);
  const stages: IStage[] = [
    { key: 'assembled', passed: document.state !== EpdState.AwaitingWarehouse },
    {
      key: 'draft',
      passed: !!t1 && DRAFT_READY.includes(t1.status),
      date: t1?.status === EpdTitleStatus.AwaitingSignature ? t1.status_changed_at : undefined,
    },
    { key: 'clientSigned', passed: isSent(t1), date: sentDate(t1) },
    { key: 'forwarderSigned', passed: isSent(t2), date: sentDate(t2) },
    { key: 'receiptSent', passed: isSent(receiptTitle), date: sentDate(receiptTitle) },
    final,
  ];
  const current =
    final.key === 'completed' ? stages.findIndex((stage) => !stage.passed) : final.passed ? -1 : stages.length - 1;
  const currentState = current >= 4 && receipt && final.key === 'completed' ? receipt.state : document.state;
  const failed = FAILED_STATES.includes(currentState);
  const accent = failed ? token.colorError : token.colorPrimary;
  const currentStyles = {
    icon: {
      background: token.colorBgContainer,
      borderWidth: token.lineWidthBold,
      borderColor: accent,
      color: accent,
      boxShadow: `0 0 0 ${token.controlOutlineWidth * 2}px ${failed ? token.colorErrorBg : token.colorPrimaryBg}`,
    },
    title: { fontWeight: token.fontWeightStrong },
  };

  return (
    <Steps
      size="small"
      titlePlacement="vertical"
      current={current < 0 ? stages.length : current}
      items={stages.map((stage, index) => ({
        title: epdT.t(`stages.${stage.key}` as const),
        status: stage.passed ? 'finish' : index === current ? (failed ? 'error' : 'process') : 'wait',
        content: stage.passed && stage.date ? formatDateAndTime(fromUtc(stage.date)) : undefined,
        styles: stage.passed
          ? { icon: { background: token.colorPrimary, color: token.colorTextLightSolid } }
          : index === current
          ? currentStyles
          : undefined,
      }))}
    />
  );
};
